/**
 * self-signup (design section 4): Cloudflare Turnstile in the portal, and the hand-off of its token to
 * coreway-billing (`POST /billing/captcha`), which verifies it with Cloudflare and records a single-use pass
 * that `agency_self_create(..., p_captcha_token)` consumes.
 *
 * The script is loaded once, with `render=explicit`, and the widget is rendered into a ref'd element. No
 * element may carry `id="turnstile"`: the browser would expose it as `window.turnstile` and shadow the API.
 */
import type { RpcError } from '@/data/rpc';

export const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** The action the server requires in Cloudflare's siteverify answer. */
export const TURNSTILE_ACTION = 'self_signup';

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  callback?(token: string): void;
  'expired-callback'?(): void;
  'error-callback'?(): void;
  theme?: 'auto' | 'light' | 'dark';
  language?: string;
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** The Turnstile API, loading its script the first time only. A failed load can be retried. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile did not load')));
    script.onerror = () => reject(new Error('turnstile did not load'));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    loading = null;
    throw err;
  });
  return loading;
}

/** Test seam: forget the cached load. */
export function resetTurnstileLoader(): void {
  loading = null;
}

/**
 * Hands the token to coreway-billing. 204 = a pass is recorded; 400 = Cloudflare said no; anything else
 * (503, a network failure) = the check is unavailable. The error codes go through `describeRpcError`.
 */
export async function verifyCaptcha(
  url: string,
  token: string,
  fetchImpl: typeof fetch = (...args) => window.fetch(...args),
): Promise<{ error: RpcError | null }> {
  try {
    const res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (res.status === 204 || res.ok) return { error: null };
    if (res.status === 400) return { error: { code: 'billing:captcha_failed', message: 'captcha failed' } };
    return { error: { code: 'billing:captcha_unavailable', message: `captcha check answered ${res.status}` } };
  } catch (err) {
    console.warn('captcha check unreachable', err);
    return { error: { code: 'billing:captcha_unavailable', message: 'captcha check unreachable' } };
  }
}
